import { createContext } from 'preact';
import { useContext, useState } from 'preact/hooks';

interface Settings {
  textSizeIndex: number;
  fontFamily: string;
  firstLineIndent: boolean;
  ttsEnabled: boolean;
  ttsRate: number | string;
  ttsVoice: string;
}

interface SettingsContextType {
  settings: Settings;
  updateSettings: (newSettings: Partial<Settings>) => void;
}

const defaultSettings: Settings = {
  textSizeIndex: 8, // Default is 100% (middle of the range)
  fontFamily: 'default',
  firstLineIndent: false,
  ttsEnabled: false,
  ttsRate: 1,
  ttsVoice: '',
};

const getStoredSettings = (): Settings => {
  const stored = localStorage.getItem('reader-settings');
  return stored ? JSON.parse(stored) : defaultSettings;
};

export const SettingsContext = createContext<SettingsContextType>({
  settings: defaultSettings,
  updateSettings: () => {},
});

export const SettingsProvider = ({
  children,
}: {
  children: preact.ComponentChildren;
}) => {
  const [settings, setSettings] = useState<Settings>(getStoredSettings);

  const updateSettings = (newSettings: Partial<Settings>) => {
    const updated = { ...settings, ...newSettings };
    setSettings(updated);
    localStorage.setItem('reader-settings', JSON.stringify(updated));
  };

  return (
    <SettingsContext.Provider value={{ settings, updateSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
